/* eslint no-param-reassign: "error" */
import {
  createSlice,
} from '@reduxjs/toolkit';

import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { PageJson } from '@/types/domain/childNotebook/entities/pageEntity';
import type { ChildNotebookStateType } from '@/types/domain/childNotebook/redux';
import type { DefecationJson } from '@/types/domain/defecation/entities/defecationEntity';
import type { DrugJson } from '@/types/domain/drug/entities/drugEntity';
import type { MealJson } from '@/types/domain/meal/entities/mealEntity';
import type { MilkJson } from '@/types/domain/milk/entities/milkEntity';
import type { SleepJson } from '@/types/domain/sleep/entities/sleepEntity';
import type { TemperatureJson } from '@/types/domain/temperature/entities/temperatureEntity';
import type {
  PayloadAction,
} from '@reduxjs/toolkit';

import { service } from '@/share';
import { getDateString, toStringDate } from '@/utils/date';

const initialState: ChildNotebookStateType = {
  page: {},
  temperature: {},
  sleep: {},
  defecation: {},
  milk: {},
  meal: {},
  drug: {},
  date: toStringDate(new Date()),
};

const convertToList = (data: {
  [key: string]: {
    items: any[];
  };
}) => {
  const keys = Object.keys(data);
  const items = [];
  for (let i = 0; i < keys.length; i += 1) {
    for (let j = 0; j < data[keys[i]].items.length; j += 1) {
      items.push(data[keys[i]].items[j]);
    }
  }
  return { items, keys };
};

const convertToObject = (items: any[], keys: string[], sortKey: string) => {
  const newData: {
    [key: string]: {
      items: any[];
    };
  } = {};
  for (let i = 0; i < keys.length; i += 1) {
    newData[keys[i]] = {
      items: items.filter((v) => toStringDate(new Date(v.data[sortKey])) === keys[i]),
    };
  }
  return newData;
};

// 旧: getObjectForSet は加算方式だったため、重複表示の原因となる。
// 本ファイルでは置換方式(getObjectForSetReplace)のみを使用する。

// 当該日付の既存データを置換してセットする
const getObjectForSetReplace = (
  data: {
    [key: string]: {
      items: any[];
    };
  },
  payload: { items: any[], date: string },
  sortKey: string,
) => {
  const { items, keys } = convertToList(data);
  const filtered = items.filter(
    (v) => toStringDate(new Date(v.data[sortKey])) !== payload.date,
  );
  for (let i = 0; i < payload.items.length; i += 1) {
    filtered.push(payload.items[i]);
  }
  if (!(payload.date in keys)) {
    keys.push(payload.date);
  }
  return convertToObject(filtered, keys, sortKey);
};

/**
 * 連絡帳Slice
 */
export const childNotebookSlice = createSlice({
  name: 'childNotebook',
  initialState,
  reducers: {
    /** 日を設定 */
    setDate: (state, action: PayloadAction<string>) => {
      state.date = action.payload;
    },
    /** ページをセットする（当該日付で置換） */
    addPages: (state, action: PayloadAction<{ items: PageJson[], date: string }>) => {
      state.page = getObjectForSetReplace(state.page, action.payload, 'recordDate');
    },
    /** ページ更新する(Create) */
    createPageStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      const { items, keys } = convertToList(state.page);
      const newItems = service.childNotebook.createPageStore(
        items,
        action.payload,
        (v) => keys.includes(v.recordDate),
      );
      state.page = convertToObject(newItems, keys, 'recordDate');
    },
    /** ページ更新する(Update) */
    updatePageStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      const { items, keys } = convertToList(state.page);
      const newItems = service.childNotebook.updatePageStore(
        items,
        action.payload,
      );
      state.page = convertToObject(newItems, keys, 'recordDate');
    },
    /** ページ更新する(Delete) */
    deletePageStore: (
      state,
      action: PayloadAction<DeleteOutputType>,
    ) => {
      const { items, keys } = convertToList(state.page);
      const newItems = service.childNotebook.deletePageStore(
        items,
        action.payload,
      );
      state.page = convertToObject(newItems, keys, 'recordDate');
    },
    /** 体温リストをセットする（当該日付で置換） */
    setTemperature: (state, action: PayloadAction<{
      items: TemperatureJson[],
      date: string,
    }>) => {
      state.temperature = getObjectForSetReplace(state.temperature, action.payload, 'measureDateTime');
    },
    /** 体温ストア作成 */
    createTemperatureStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      const { items, keys } = convertToList(state.temperature);
      const newItems = service.temperature.createTemperatureStore(
        items,
        action.payload,
        (v) => keys.includes(getDateString(new Date(v.measureDateTime))),
      );
      state.temperature = convertToObject(newItems, keys, 'measureDateTime');
    },
    /** 体温ストア更新 */
    updateTemperatureStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      const { items, keys } = convertToList(state.temperature);
      const newItems = service.temperature.updateTemperatureStore(
        items,
        action.payload,
      );
      state.temperature = convertToObject(newItems, keys, 'measureDateTime');
    },
    /** 体温ストア削除 */
    deleteTemperatureStore: (
      state,
      action: PayloadAction<DeleteOutputType>,
    ) => {
      const { items, keys } = convertToList(state.temperature);
      const newItems = service.temperature.deleteTemperatureStore(
        items,
        action.payload,
      );
      state.temperature = convertToObject(newItems, keys, 'measureDateTime');
    },
    /** 睡眠リストをセット（当該日付で置換） */
    setSleep: (state, action: PayloadAction<{
      items: SleepJson[],
      date: string,
    }>) => {
      state.sleep = getObjectForSetReplace(state.sleep, action.payload, 'measureDateTime');
    },
    /** 睡眠ストア作成 */
    createSleepStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      const { items, keys } = convertToList(state.sleep);
      const newItems = service.sleep.createSleepStore(
        items,
        action.payload,
        (v) => keys.includes(getDateString(new Date(v.measureDateTime))),
      );
      state.sleep = convertToObject(newItems, keys, 'measureDateTime');
    },
    /** 睡眠ストア更新 */
    updateSleepStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      const { items, keys } = convertToList(state.sleep);
      const newItems = service.sleep.updateSleepStore(
        items,
        action.payload,
      );
      state.sleep = convertToObject(newItems, keys, 'measureDateTime');
    },
    /** 睡眠ストア削除 */
    deleteSleepStore: (state, action: PayloadAction<DeleteOutputType>) => {
      const { items, keys } = convertToList(state.sleep);
      const newItems = service.sleep.deleteSleepStore(
        items,
        action.payload,
      );
      state.sleep = convertToObject(newItems, keys, 'measureDateTime');
    },
    /** 排泄リストをセットする（当該日付で置換） */
    setDefecation: (state, action: PayloadAction<{
      items: DefecationJson[],
      date: string,
    }>) => {
      state.defecation = getObjectForSetReplace(state.defecation, action.payload, 'measureDateTime');
    },
    /** 排泄ストア作成 */
    createDefecationStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      const { items, keys } = convertToList(state.defecation);
      const newItems = service.defecation.createDefecationStore(
        items,
        action.payload,
        (v) => keys.includes(getDateString(new Date(v.measureDateTime))),
      );
      state.defecation = convertToObject(newItems, keys, 'measureDateTime');
    },
    /** 排泄ストア更新 */
    updateDefecationStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      const { items, keys } = convertToList(state.defecation);
      const newItems = service.defecation.updateDefecationStore(
        items,
        action.payload,
      );
      state.defecation = convertToObject(newItems, keys, 'measureDateTime');
    },
    /** 排泄ストア削除 */
    deleteDefecationStore: (
      state,
      action: PayloadAction<DeleteOutputType>,
    ) => {
      const { items, keys } = convertToList(state.defecation);
      const newItems = service.defecation.deleteDefecationStore(
        items,
        action.payload,
      );
      state.defecation = convertToObject(newItems, keys, 'measureDateTime');
    },
    /** ミルクリストをセット（当該日付で置換） */
    setMilk: (state, action: PayloadAction<{
      items: MilkJson[],
      date: string,
    }>) => {
      state.milk = getObjectForSetReplace(state.milk, action.payload, 'measureDateTime');
    },
    /** ミルクストア作成 */
    createMilkStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      const { items, keys } = convertToList(state.milk);
      const newItems = service.milk.createMilkStore(
        items,
        action.payload,
        (v) => keys.includes(getDateString(new Date(v.measureDateTime))),
      );
      state.milk = convertToObject(newItems, keys, 'measureDateTime');
    },
    /** ミルクストア更新 */
    updateMilkStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      const { items, keys } = convertToList(state.milk);
      const newItems = service.milk.updateMilkStore(
        items,
        action.payload,
      );
      state.milk = convertToObject(newItems, keys, 'measureDateTime');
    },
    /** ミルクストア削除 */
    deleteMilkStore: (state, action: PayloadAction<DeleteOutputType>) => {
      const { items, keys } = convertToList(state.milk);
      const newItems = service.milk.deleteMilkStore(
        items,
        action.payload,
      );
      state.milk = convertToObject(newItems, keys, 'measureDateTime');
    },
    /** 食事リストをセットする（当該日付で置換） */
    setMeal: (state, action: PayloadAction<{
      items: MealJson[],
      date: string,
    }>) => {
      state.meal = getObjectForSetReplace(state.meal, action.payload, 'measureDateTime');
    },
    /** 食事ストア作成 */
    createMealStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      const { items, keys } = convertToList(state.meal);
      const newItems = service.meal.createMealStore(
        items,
        action.payload,
        (v) => keys.includes(getDateString(new Date(v.measureDateTime))),
      );
      state.meal = convertToObject(newItems, keys, 'measureDateTime');
    },
    /** 食事ストア更新 */
    updateMealStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      const { items, keys } = convertToList(state.meal);
      const newItems = service.meal.updateMealStore(
        items,
        action.payload,
      );
      state.meal = convertToObject(newItems, keys, 'measureDateTime');
    },
    /** 食事ストア削除 */
    deleteMealStore: (
      state,
      action: PayloadAction<DeleteOutputType>,
    ) => {
      const { items, keys } = convertToList(state.meal);
      const newItems = service.meal.deleteMealStore(
        items,
        action.payload,
      );
      state.meal = convertToObject(newItems, keys, 'measureDateTime');
    },
    /** 薬リストをセットする（当該日付で置換） */
    setDrug: (state, action: PayloadAction<{
      items: DrugJson[],
      date: string,
    }>) => {
      state.drug = getObjectForSetReplace(state.drug, action.payload, 'measureDateTime');
    },
    /** 薬ストア作成 */
    createDrugStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      const { items, keys } = convertToList(state.drug);
      const newItems = service.drug.createDrugStore(
        items,
        action.payload,
        (v) => keys.includes(getDateString(new Date(v.measureDateTime))),
      );
      state.drug = convertToObject(newItems, keys, 'measureDateTime');
    },
    /** 薬ストア更新 */
    updateDrugStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      const { items, keys } = convertToList(state.drug);
      const newItems = service.drug.updateDrugStore(
        items,
        action.payload,
      );
      state.drug = convertToObject(newItems, keys, 'measureDateTime');
    },
    /** 薬ストア削除 */
    deleteDrugStore: (
      state,
      action: PayloadAction<DeleteOutputType>,
    ) => {
      const { items, keys } = convertToList(state.drug);
      const newItems = service.drug.deleteDrugStore(
        items,
        action.payload,
      );
      state.drug = convertToObject(newItems, keys, 'measureDateTime');
    },
  },
});
